import React, { useState, useEffect } from "react";
import axios from "axios";
import RecruiterCard from "./RecruiterCard";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
  Box,
  styled,
  alpha,
  Grid,
  Select,
  FormControl,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItem,
  Paper,
  Divider,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import {
  CreateOutlined,
  DeleteOutline,
  EmailOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  PersonOutlined,
  VisibilityOutlined,
  PhoneOutlined,
  WorkOutlined,
  LanguageOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  SelectAllOutlined,
  DeselectOutlined,
  MoreVertOutlined,
  DeleteSweepOutlined,
  EditOutlined,
  SendOutlined,
  BlockOutlined,
  Add,
} from "@mui/icons-material";
import { COLORS } from "colors/color";
import { List as ListIcon } from "bootstrap-icons-react";
import RecruiterDetails from "./RecruiterDetails";
import SmartHireLoader from "components/Loading/SmartHireLoader";
import { useTranslation } from "react-i18next";
import colors from "colors/color";

const apiUrl = process.env.REACT_APP_API_URL;
const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;

// Styled components for enhanced UI
const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "transparent", // Make main container transparent
  minHeight: "100vh",
}));
const ResponsiveTypography = styled(Typography)(({ theme, variant }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: variant === 'h5' ? '1.25rem' :
      variant === 'h6' ? '1.1rem' :
        variant === 'body2' ? '0.8rem' : 'inherit',
  },
}));
const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  background: "linear-gradient(135deg, #ffffff 0%, #fafbff 100%)",
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
  boxShadow: `0 8px 32px ${alpha(colors.primary, 0.08)}`,
}));

const BulkActionBar = styled(Paper)(({ theme, visible }) => ({
  position: "fixed",
  bottom: visible ? 20 : -80,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1000,
  padding: theme.spacing(2, 3),
  borderRadius: 20,
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${alpha(
    colors.primary,
    0.9
  )} 100%)`,
  color: "white",
  boxShadow: `0 12px 40px ${alpha(colors.primary, 0.3)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: active ? alpha(colors.primary, 0.1) : "transparent",
  color: active ? colors.primary : alpha(colors.primary, 0.5),
  border: `1px solid ${active ? colors.primary : alpha(colors.primary, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(colors.primary, 0.05),
    color: colors.primary,
  },
}));

const StyledList = styled(List)(({ theme }) => ({
  padding: 0,
  "& .MuiListItem-root": {
    padding: 0,
    marginBottom: theme.spacing(2),

  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr", // Single column for one recruiter per line
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: "transparent",
  border: "none",
  boxShadow: 'none'
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#ffffff",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
      "& fieldset": {
        borderColor: colors.primary,
        borderWidth: 2,
      },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.primary,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "#64748b",
  fontSize: "14px",
  fontFamily: "Poppins",
  fontWeight: 500,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#ffffff",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
      "& fieldset": {
        borderColor: colors.primary,
        borderWidth: 2,
      },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.primary,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    backgroundColor: colors.primary,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '1rem',
    minHeight: 48,
    color: alpha(colors.primary, 0.6),
    '&.Mui-selected': {
      color: colors.primary,
    },
    '&:hover': {
      color: colors.primary,
      backgroundColor: alpha(colors.primary, 0.04),
    },
  },
}));

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
}));

function ListRecruiter() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedRecruiterId, setSelectedRecruiterId] = useState(null);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // New state for enhanced UI
  const [selectedRecruiters, setSelectedRecruiters] = useState(new Set());
  const [activeTab, setActiveTab] = useState("active"); // 'active' or 'inactive'

  const filteredRecruiters = recruiters
    .filter((recruiter) => {
      const matchesActiveStatus = activeTab === "active" ? recruiter.is_active : !recruiter.is_active;
      return matchesActiveStatus;
    })
    .sort((a, b) => {
      // Sort by name within the filtered group
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Get counts for tabs
  const activeRecruitersCount = recruiters.filter(r => r.is_active).length;
  const inactiveRecruitersCount = recruiters.filter(r => !r.is_active).length;

  const [newRecruiter, setNewRecruiter] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    gender: "",
    company: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    comp_industry: "",
    comp_description: "",
    image: null,
  });

  // Email validation state
  const [emailError, setEmailError] = useState(false);
  const [emailHelperText, setEmailHelperText] = useState("");

  // Utility functions
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const senderId = decodedToken.user_id;
      return senderId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  const adminId = getUserIdFromAccessToken();

  // Clear selection when switching tabs
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedRecruiters(new Set()); // Clear selection when switching tabs
  };

  // Selection handlers
  const handleToggleSelection = (recruiterId) => {
    const newSelection = new Set(selectedRecruiters);
    if (newSelection.has(recruiterId)) {
      newSelection.delete(recruiterId);
    } else {
      newSelection.add(recruiterId);
    }
    setSelectedRecruiters(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedRecruiters.size === filteredRecruiters.length) {
      setSelectedRecruiters(new Set());
    } else {
      setSelectedRecruiters(new Set(filteredRecruiters.map((r) => r.id)));
    }
  };

  // Update the clear selection to handle list view properly
  const handleClearSelection = () => {
    setSelectedRecruiters(new Set());
  };

  // Bulk actions

  const handleBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedRecruiters).map((recruiterId) =>
        axios.delete(
          `${apiUrl}recruiters/admin/${adminId}/recruiters/${recruiterId}/delete/`
        )
      );
      await Promise.all(deletePromises);
      handleClearSelection();
      fetchRecruiters();
    } catch (error) {
      console.error("Failed to delete recruiters:", error);
    }
  };

  const handleBulkEmail = () => {
    // Implementation for bulk email functionality
    console.log("Bulk email to:", Array.from(selectedRecruiters));
  };

  const handleBulkDeactivate = () => {
    // Implementation for bulk deactivate functionality
    console.log("Bulk deactivate:", Array.from(selectedRecruiters));
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if all required fields are valid
  const isFormValid = () => {
    return (
      newRecruiter.first_name.trim() !== "" &&
      newRecruiter.last_name.trim() !== "" &&
      newRecruiter.email.trim() !== "" &&
      validateEmail(newRecruiter.email) &&
      newRecruiter.gender !== ""
    );
  };

  // Data handlers
  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewRecruiter((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Validate email when email field changes
    if (name === "email") {
      if (value.trim() === "") {
        setEmailError(false);
        setEmailHelperText("");
      } else if (!validateEmail(value)) {
        setEmailError(true);
        setEmailHelperText(t('listRecruiter.emailValidation.invalidEmail'));
      } else {
        setEmailError(false);
        setEmailHelperText("");
      }
    }
  };

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}recruiters/admin/${adminId}/recruiters/`
      );
      setRecruiters(response.data);
      console.log("data 212: ", response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch recruiters");
      setLoading(false);
    }
  };

  const handleDeleteRecruiter = async (recruiterId) => {
    try {
      await axios.delete(
        `${apiUrl}recruiters/admin/${adminId}/recruiters/${recruiterId}/delete/`
      );
      fetchRecruiters();
      setSelectedRecruiterId(null);
    } catch (error) {
      console.error("Failed to delete recruiter", error);
    }
  };

  const handleUpdateRecruiter = async (updatedRecruiterData) => {
    try {
      await axios.put(
        `${apiUrl}recruiters/admin/${adminId}/recruiters/${updatedRecruiterData.recruiterId}/update/`,
        updatedRecruiterData
      );
      fetchRecruiters();
    } catch (error) {
      console.error("Failed to update recruiter", error);
    }
  };

  const handleSelectRecruiter = (recruiterId) => {
    console.log(`Selected recruiter: ${recruiterId}`);
    setSelectedRecruiterId(recruiterId);
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      password += charset.charAt(Math.floor(Math.random() * n));
    }
    return password;
  };
  const [openDelete, setOpenDelete] = useState(false);
  
  const handleCreateRecruiter = async () => {
    const generatedPassword = generateRandomPassword();

    setNewRecruiter({
      ...newRecruiter,
      username: `${newRecruiter.first_name}_${newRecruiter.last_name}`,
    });
    console.log("newReruiter info", newRecruiter);
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}recruiters/create_recruiter_admin/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...newRecruiter,
            password: generatedPassword,
            adminId: adminId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Recruiter created successfully:", result);
      setOpen(false)
      setOpenCreate(false);
      setNewRecruiter({
        username: "",
        first_name: "",
        last_name: "",
        email: "",
        gender: "",
        company: "",
        address: "",
        city: "",
        country: "",
        postal_code: "",
        comp_industry: "",
        comp_description: "",
        image: null,
      });
      setEmailError(false);
      setEmailHelperText("");
      fetchRecruiters();
    } catch (error) {
      console.error("Failed to create recruiter:", error);
    }
  };

  const handleBackToList = () => {
    setSelectedRecruiterId(null);
  };

  const handleSearch = () => {
    console.log("Search triggered");
  };

  // Render helper
  const renderRecruiterCard = (recruiter) => (
    <RecruiterCard
      key={recruiter.id}
      recruiterId={recruiter.id}
      imageSrc={recruiter.image || "default-image-url"}
      firstName={recruiter.first_name}
      lastName={recruiter.last_name}
      email={recruiter.email}
      company={recruiter.company}
      address={recruiter.address}
      city={recruiter.city}
      country={recruiter.country}
      postal_code={recruiter.postal_code}
      comp_industry={recruiter.comp_industry}
      comp_description={recruiter.comp_description}
      onDeleteRecruiter={handleDeleteRecruiter}
      updateRecruiter={handleUpdateRecruiter}
      onSelectRecruiter={handleSelectRecruiter}
      viewMode='list'
      isActive={recruiter.is_active || false} // Pass the is_active status
      isSelectable={true} // Always selectable in list view
      isSelected={selectedRecruiters.has(recruiter.id)}
      onToggleSelection={handleToggleSelection}
    />
  );

  // Render recruiter list content
  const renderRecruiterList = () => {
    if (filteredRecruiters.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: alpha(colors.primary, 0.6),
              fontFamily: 'Poppins',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {t('listRecruiter.noRecruitersFound', { status: activeTab })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: alpha(colors.primary, 0.4),
              fontFamily: 'Poppins',
            }}
          >
            {t('listRecruiter.noRecruitersAvailable', { status: activeTab })}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <GridContainer>
          {filteredRecruiters.map((recruiter) => (
            <Box key={recruiter.id}>
              {renderRecruiterCard(recruiter)}
            </Box>
          ))}
        </GridContainer>
      </>
    );
  };

  useEffect(() => {
    fetchRecruiters();
  }, []);

  if (loading) return <SmartHireLoader />;
  if (error) return <div>{error}</div>;

  return (
    <MainContainer>
      {selectedRecruiterId ? (
        <RecruiterDetails
          recruiterId={selectedRecruiterId}
          onBack={handleBackToList}
        />
      ) : (
        <>
          {/* Header Section */}
          <Box>
            {/* Controls Bar - Tabs and Add Button on same level */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                mb: 3,
              }}
            >
              {/* Tabs for Active/Inactive Recruiters */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StyledTabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    minHeight: 40,
                    '& .MuiTabs-indicator': {
                      height: 2,
                    },
                    '& .MuiTab-root': {
                      minHeight: 40,
                      py: 1,
                      px: 3,
                      minWidth: 150,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <Tab
                    value="active"
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {t('listRecruiter.activeRecruiters')}
                        </Typography>
                        <Badge
                          badgeContent={activeRecruitersCount}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: colors.primary,
                              color: 'white',
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              minWidth: 18,
                              height: 18,
                            },
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab
                    value="inactive"
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {t('listRecruiter.inactiveRecruiters')}
                        </Typography>
                        <Badge
                          badgeContent={inactiveRecruitersCount}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: '#ef4444',
                              color: 'white',
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              minWidth: 18,
                              height: 18,
                            },
                          }}
                        />
                      </Box>
                    }
                  />
                </StyledTabs>
              </Box>

              {/* Add Button */}
              <Button
                onClick={() => setOpen(true)}
                variant="contained"
                startIcon={<Add />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.primary
                    } 0%, ${alpha(colors.primary, 0.8)} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${alpha(
                      colors.primary,
                      0.9
                    )} 0%, ${colors.primary} 100%)`,
                  },
                }}
              >
                {t('listRecruiter.addRecruiter')}
              </Button>
            </Box>
          </Box>

          <TabPanel>
            <Stack>
              <Stack direction="row" spacing={1} alignItems="center"
                sx={{
                  ml: 3,
                  mb: 2
                }}>
                {filteredRecruiters.length > 0 && (
                  <Tooltip
                    title={
                      selectedRecruiters.size === filteredRecruiters.length
                        ? t('listRecruiter.deselectAll')
                        : t('listRecruiter.selectAll')
                    }
                  >
                    <IconButton
                      onClick={handleSelectAll}
                      size="medium"
                      sx={{
                        color: colors.primary,
                        backgroundColor: alpha(colors.primary, 0.05),
                        "&:hover": {
                          backgroundColor: alpha(colors.primary, 0.1),
                        },
                      }}
                    >
                      {selectedRecruiters.size === filteredRecruiters.length ? (
                        <CheckBoxIcon fontSize="small" />
                      ) : (
                        <CheckBoxOutlineBlankIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Chip
                  label={t('listRecruiter.recruiterSelected', { count: selectedRecruiters.size })}
                  color="primary"
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`${filteredRecruiters.length} ${activeTab} ${t('navbar.recruiter')}${filteredRecruiters.length !== 1 ? 's' : ''}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    borderColor: alpha(colors.primary, 0.3),
                    color: alpha(colors.primary, 0.8),
                  }}
                />
              </Stack>
            </Stack>

            {renderRecruiterList()}
          </TabPanel>

          {/* Bulk Action Bar */}
          <BulkActionBar visible={selectedRecruiters.size > 0}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {t('listRecruiter.recruiterSelected', { count: selectedRecruiters.size })}
            </Typography>

            <Stack direction="row" spacing={1}>

              <Tooltip title={t('listRecruiter.bulkActions.deactivateSelected')}>
                <IconButton
                  size="small"
                  onClick={() => setOpenDelete(true)}
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ef4444", 0.8),
                    "&:hover": {
                      backgroundColor: "#ef4444",
                    },
                  }}
                >
                  <BlockOutlined fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('listRecruiter.bulkActions.clearSelection')}>
                <IconButton
                  size="small"
                  onClick={handleClearSelection}
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ffffff", 0.1),
                    "&:hover": {
                      backgroundColor: alpha("#ffffff", 0.2),
                    },
                  }}
                >
                  <DeselectOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </BulkActionBar>
        </>
      )}

      {/* Create Recruiter Dialog */}
      
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent sx={{ padding: { xs: 3, sm: 2 } }}>
          <ResponsiveTypography fontSize={{ xs: 16, sm: 18 }}>
            {t('listRecruiter.confirmations.blockRecruiter')}
          </ResponsiveTypography>
        </DialogContent>
        <DialogActions sx={{ padding: { xs: 2, sm: 1 } }}>
          <Button variant="contained" onClick={() => setOpenDelete(false)}
            >
            {t('listRecruiter.confirmations.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleBulkDelete();
              setOpenDelete(false);
            }}
            color="error"
          >
            {t('listRecruiter.confirmations.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 20px 60px ${alpha(colors.primary, 0.15)}`,
          },
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{
              color: colors.primary,
              fontFamily: "Poppins",
              fontWeight: 700,
              mb: 3,
            }}
          >
            {t('createRecruiter.title')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTypography>{t('createRecruiter.fields.firstName')} *</StyledTypography>
              <StyledTextField
                fullWidth
                name="first_name"
                value={newRecruiter.first_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTypography>{t('createRecruiter.fields.lastName')} *</StyledTypography>
              <StyledTextField
                fullWidth
                name="last_name"
                value={newRecruiter.last_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTypography>{t('createRecruiter.fields.email')} *</StyledTypography>
              <StyledTextField
                fullWidth
                type="email"
                name="email"
                value={newRecruiter.email}
                onChange={handleChange}
                error={emailError}
                helperText={emailHelperText}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    // borderRadius: 12,
                    backgroundColor: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 4px 20px ${alpha(colors.primary, 0.08)}`,
                    },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                      "& fieldset": {
                        borderColor: emailError ? "#d32f2f" : colors.primary,
                        borderWidth: 2,
                      },
                    },
                    "&.Mui-error": {
                      "& fieldset": {
                        borderColor: "#d32f2f",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: emailError ? "#d32f2f" : colors.primary,
                  },
                  "& .MuiFormHelperText-root": {
                    fontFamily: "Poppins",
                    fontSize: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTypography>
                {t("createRecruiter.fields.gender")} *
              </StyledTypography>
              <StyledFormControl fullWidth required>
                <Select
                  labelId="gender-label"
                  name="gender"
                  value={newRecruiter.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="f">
                    {t("createRecruiter.genderOptions.ms")}
                  </MenuItem>
                  <MenuItem value="m">
                    {t("createRecruiter.genderOptions.mr")}
                  </MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
            }}
          >
            {t('general.cancel')}
          </Button>
          <Button
            onClick={handleCreateRecruiter}
            variant="contained"
            disabled={!isFormValid()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
              background: !isFormValid()
                ? "#e0e0e0"
                : `linear-gradient(135deg, ${colors.primary
                } 0%, ${alpha(colors.primary, 0.8)} 100%)`,
              color: !isFormValid() ? "#9e9e9e" : "white",
              "&:hover": {
                background: !isFormValid()
                  ? "#e0e0e0"
                  : `linear-gradient(135deg, ${alpha(
                    colors.primary,
                    0.9
                  )} 0%, ${colors.primary} 100%)`,
              },
              "&:disabled": {
                background: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {t('createRecruiter.buttons.createRecruiter')}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}

export default ListRecruiter;
